/* ==========================================================================
   WAENN — Carrusel de looks ("Shop the look")
   ==========================================================================
   1) Efecto coverflow: en cada scroll/resize, calcula qué tan lejos está
      cada look del centro del carrusel y lo guarda en la variable CSS
      --dist de ese elemento (0 = centrado, 1 = borde). El tamaño/opacidad
      los aplica el CSS con calc(), aquí solo se mide la distancia.
   2) El "enganche" al centro (snap) lo hace el navegador solo, vía CSS
      scroll-snap-type — este script NO fuerza posiciones de scroll salvo
      cuando navegas con Anterior/Siguiente dentro del panel, o con teclado.
   3) Al hacer click en un look, se abre un panel a pantalla completa con
      los datos ya incrustados en la página (script JSON por sección, sin
      llamadas adicionales) y permite añadir todas las prendas del look al
      carrito en una sola llamada a cart/add.js, igual que el resto de la
      tienda.
   4) Loop infinito: la fila de looks se triplica (copia-antes + original +
      copia-después) y, cuando el usuario se acerca al final de alguna de
      las copias, el scroll se recoloca de forma instantánea al punto
      equivalente de la copia central — como las tres copias son idénticas,
      el salto no se nota y los looks parecen rotar sin principio ni fin.
   ========================================================================== */

(function () {
  'use strict';

  function initInstance(root) {
    var viewport = root.querySelector('[data-waenn-looks]');
    var track = root.querySelector('[data-waenn-looks-track]');
    var dataScript = root.querySelector('[data-waenn-looks-data]');
    var panel = root.querySelector('[data-waenn-look-panel]');
    if (!viewport || !track || !dataScript || !panel) return;

    // El panel usa position:fixed para cubrir toda la pantalla, pero esta
    // seccion puede vivir dentro de la portada con el efecto de scroll "por
    // paginas" activo (assets/section-scroll-snap.js), que envuelve las
    // secciones en un contenedor con transform:translateY(). Un ancestro
    // con transform convierte ese transform en el "contenedor de referencia"
    // de cualquier descendiente position:fixed (asi lo define CSS), asi que
    // el panel dejaba de fijarse a la pantalla real y se posicionaba
    // relativo a esa seccion en movimiento -- aparecia desplazado y con una
    // altura enorme en vez de cubrir el viewport. Sacarlo al final de
    // <body> (fuera de cualquier ancestro con transform) garantiza que
    // position:fixed siempre se calcule contra la ventana real, se use o no
    // el scroll por secciones.
    document.body.appendChild(panel);

    var looks = [];
    try {
      looks = JSON.parse(dataScript.textContent) || [];
    } catch (e) {
      looks = [];
    }

    /* -------------------- Loop infinito -------------------- */
    /* El scroll-snap nativo no soporta "bucle" por sí solo: siempre hay un
       primer y un último elemento reales. El truco estándar para simular
       scroll infinito es triplicar la fila (copia-antes + original +
       copia-después) y, en cuanto el usuario se acerca al final de una
       copia, recolocar el scroll de forma instantánea (sin animación) al
       punto equivalente de la copia central — como las copias son
       idénticas, el salto es imperceptible y el usuario nota solo que los
       looks "siguen rotando" sin fin en cualquier dirección. */
    var originalSlideNodes = Array.prototype.slice.call(track.children);
    var lookCount = originalSlideNodes.length;
    var middleOffset = 0;

    if (lookCount > 1) {
      var firstOriginal = track.firstChild;
      originalSlideNodes.forEach(function (node) {
        track.insertBefore(node.cloneNode(true), firstOriginal);
      });
      originalSlideNodes.forEach(function (node) {
        track.appendChild(node.cloneNode(true));
      });
      middleOffset = lookCount;
    }

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-waenn-look-open]'));
    var ticking = false;

    /* -------------------- Efecto coverflow (distancia al centro) -------------------- */

    function updateScales() {
      ticking = false;
      var vpRect = viewport.getBoundingClientRect();
      var centerX = vpRect.left + vpRect.width / 2;

      slides.forEach(function (item) {
        var rect = item.getBoundingClientRect();
        var itemCenter = rect.left + rect.width / 2;
        var raw = Math.abs(itemCenter - centerX);
        var norm = vpRect.width / 2 + rect.width / 2;
        var dist = norm > 0 ? Math.min(raw / norm, 1) : 0;
        item.style.setProperty('--dist', dist.toFixed(3));
      });
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateScales);
    }

    /* Recoloca el scroll cuando el usuario se acerca al final de alguna de
       las copias, para que nunca "toque" un borde real. Se hace con un
       pequeño retraso tras el último evento de scroll (no en cada frame)
       para no interrumpir el gesto/inercia mientras el usuario aún se
       está desplazando. */
    var loopTimer = null;
    function scheduleLoopCheck() {
      if (lookCount <= 1) return;
      if (loopTimer) clearTimeout(loopTimer);
      loopTimer = setTimeout(function () {
        var setWidth = track.scrollWidth / 3;
        if (!setWidth) return;
        if (viewport.scrollLeft < setWidth) {
          viewport.scrollLeft += setWidth;
        } else if (viewport.scrollLeft >= setWidth * 2) {
          viewport.scrollLeft -= setWidth;
        }
      }, 120);
    }

    function onViewportScroll() {
      requestUpdate();
      scheduleLoopCheck();
    }

    viewport.addEventListener('scroll', onViewportScroll, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);

    // Arranca el scroll en la copia central: así hay un tramo entero de
    // looks disponible para desplazarse tanto a la izquierda como a la
    // derecha antes de necesitar el primer "salto" invisible.
    if (lookCount > 1) {
      viewport.scrollLeft = track.scrollWidth / 3;
    }
    requestUpdate();
    // Las imágenes pueden cambiar el ancho del layout al cargar; se
    // recalcula una vez más pasado un instante por seguridad.
    setTimeout(function () {
      if (lookCount > 1) {
        viewport.scrollLeft = track.scrollWidth / 3;
      }
      requestUpdate();
    }, 400);

    function scrollToIndex(index, behavior) {
      var target = slides[index];
      if (!target) return;
      target.scrollIntoView({
        behavior: behavior || 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }

    // Navega siempre a la copia "central" de un look concreto (por su
    // índice de datos, no su posición en el DOM): esa copia es la que
    // siempre existe de forma fiable, sea cual sea la copia sobre la que
    // se hizo click originalmente.
    function scrollToLookIndex(lookIndex, behavior) {
      scrollToIndex(middleOffset + lookIndex, behavior);
    }

    /* -------------------- Navegación por teclado (flechas) -------------------- */

    slides.forEach(function (item, i) {
      item.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(Math.min(i + 1, slides.length - 1));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(Math.max(i - 1, 0));
        }
      });
    });

    /* -------------------- Panel "Ver el look" -------------------- */

    var panelImage = panel.querySelector('[data-waenn-look-panel-image]');
    var panelTitle = panel.querySelector('[data-waenn-look-panel-title]');
    var panelProducts = panel.querySelector('[data-waenn-look-panel-products]');
    var panelError = panel.querySelector('[data-waenn-look-panel-error]');
    var addAllBtn = panel.querySelector('[data-waenn-look-add-all]');
    var addAllText = panel.querySelector('[data-waenn-look-add-all-text]');
    var prevBtn = panel.querySelector('[data-waenn-look-prev]');
    var nextBtn = panel.querySelector('[data-waenn-look-next]');
    var closeEls = panel.querySelectorAll('[data-waenn-look-close]');
    var openIndex = null;
    var lastFocusedEl = null;

    function money(value) {
      return value;
    }

    function renderProduct(product) {
      var row = document.createElement('div');
      row.className = 'waenn-look-panel__product';

      var img = document.createElement('img');
      img.src = product.image || '';
      img.alt = product.title || '';
      row.appendChild(img);

      var info = document.createElement('div');
      info.className = 'waenn-look-panel__product-info';

      var title = document.createElement('p');
      title.className = 'waenn-look-panel__product-title';
      var link = document.createElement('a');
      link.href = product.url || '#';
      link.textContent = product.title || '';
      title.appendChild(link);
      info.appendChild(title);

      var price = document.createElement('p');
      price.className = 'waenn-look-panel__product-price';
      price.textContent = money(product.price || '');
      info.appendChild(price);

      var variants = (product.variants || []).filter(function (v) {
        return v.available;
      });
      var allVariants = product.variants || [];
      var usableVariants = variants.length > 0 ? variants : allVariants;

      if (usableVariants.length > 1) {
        var select = document.createElement('select');
        select.className = 'waenn-look-panel__product-size';
        select.setAttribute('data-variant-select', '');
        usableVariants.forEach(function (variant) {
          var option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title;
          select.appendChild(option);
        });
        info.appendChild(select);
      } else if (usableVariants.length === 1) {
        // IMPORTANTE: este atributo se lee luego con row.getAttribute(...)
        // en el listener de "Añadir todo a la cesta" (más abajo), NO con
        // info.getAttribute(...) -- info es un hijo de row, no el propio
        // row. Antes se ponia en "info" y el listener SIEMPRE lo
        // encontraba vacio en "row" para cualquier prenda de variante
        // unica (p.ej. anillos/bolsos sin tallas): el boton daba "falta
        // seleccionar talla" aunque las demas prendas SI tuvieran su
        // talla elegida, y el carrito nunca llegaba a añadirse.
        row.setAttribute('data-single-variant-id', usableVariants[0].id);
      }

      if (variants.length === 0) {
        var soldOut = document.createElement('p');
        soldOut.className = 'waenn-look-panel__product-price';
        soldOut.textContent = 'Agotado';
        info.appendChild(soldOut);
      }

      row.setAttribute('data-product-row', '');
      row.appendChild(info);
      return row;
    }

    function openPanel(index) {
      var look = looks[index];
      if (!look) return;
      openIndex = index;

      panelImage.src = look.image || '';
      panelImage.alt = look.name || '';
      panelTitle.textContent = look.name || '';
      panelProducts.innerHTML = '';
      panelError.hidden = true;

      (look.products || []).forEach(function (product) {
        panelProducts.appendChild(renderProduct(product));
      });

      prevBtn.disabled = looks.length <= 1;
      nextBtn.disabled = looks.length <= 1;

      lastFocusedEl = document.activeElement;
      panel.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      panel.querySelector('.waenn-look-panel__close').focus();

      // Se usa siempre la copia central del carrusel como referencia: da
      // igual sobre qué copia (antes/original/después) se hizo click,
      // como las tres son idénticas el salto de fondo no se nota (el
      // panel a pantalla completa ya lo tapa).
      scrollToLookIndex(index, 'instant');
    }

    function closePanel() {
      panel.hidden = true;
      openIndex = null;
      document.documentElement.style.overflow = '';
      if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
        lastFocusedEl.focus();
      }
    }

    function navigate(step) {
      if (openIndex === null || looks.length === 0) return;
      var next = (openIndex + step + looks.length) % looks.length;
      openPanel(next);
    }

    slides.forEach(function (item) {
      // El índice de datos (data-look-index) es el mismo en las tres
      // copias del carrusel -- no confundir con la posición en el DOM
      // (que ahora, al triplicarse, ya no coincide con el índice del look).
      var lookIndex = parseInt(item.getAttribute('data-look-index'), 10);
      item.addEventListener('click', function () {
        openPanel(lookIndex);
      });
    });

    closeEls.forEach(function (el) {
      el.addEventListener('click', closePanel);
    });

    document.addEventListener('keydown', function (event) {
      if (panel.hidden) return;
      if (event.key === 'Escape') closePanel();
      if (event.key === 'ArrowRight') navigate(1);
      if (event.key === 'ArrowLeft') navigate(-1);
    });

    prevBtn.addEventListener('click', function () {
      navigate(-1);
    });
    nextBtn.addEventListener('click', function () {
      navigate(1);
    });

    /* -------------------- Añadir todas las prendas al carrito -------------------- */

    addAllBtn.addEventListener('click', function () {
      var rows = Array.prototype.slice.call(panelProducts.querySelectorAll('[data-product-row]'));
      var items = [];
      var missing = false;

      rows.forEach(function (row) {
        var select = row.querySelector('[data-variant-select]');
        var variantId = select ? select.value : row.getAttribute('data-single-variant-id');
        if (!variantId) {
          missing = true;
          return;
        }
        items.push({ id: variantId, quantity: 1 });
      });

      if (missing || items.length === 0) {
        panelError.hidden = false;
        return;
      }

      panelError.hidden = true;
      addAllBtn.disabled = true;
      var originalText = addAllText.textContent;
      addAllText.textContent = 'Añadiendo...';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cartDrawerEl = document.querySelector('cart-drawer');
      var sectionsToRequest =
        cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function'
          ? cartDrawerEl.getSectionsToRender().map(function (section) {
              return section.id;
            })
          : ['cart-drawer', 'cart-icon-bubble'];

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items,
          sections: sectionsToRequest,
          sections_url: window.location.pathname
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart-add-failed');
          return res.json();
        })
        .then(function (data) {
          addAllBtn.disabled = false;
          addAllText.textContent = originalText;
          if (cartDrawerEl && data && data.sections && typeof cartDrawerEl.renderContents === 'function') {
            cartDrawerEl.renderContents(data);
          }
          closePanel();
        })
        .catch(function () {
          addAllBtn.disabled = false;
          addAllText.textContent = originalText;
          panelError.textContent = 'No se ha podido añadir el look al carrito. Inténtalo de nuevo.';
          panelError.hidden = false;
        });
    });
  }

  function init() {
    var instances = document.querySelectorAll('.waenn-looks');
    Array.prototype.forEach.call(instances, initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
